import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Rating, ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  propertyApi,
  placesApi,
  type PropertyData,
  GewType,
} from '../services';
import { useToast } from '../contexts/ToastContext';
import { LoadingSpinner } from '../components/LoadingSpinner';
import './PropertyPage.css';

const CreatePropertyPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  const emptyProperty: PropertyData = {
    id: '',
    address: '',
    country: 'Österreich',
    gain: 0,
    gainPercentage: 0,
    features: {},
    ratings: {},
  };

  const [property, setProperty] = useState<PropertyData>(emptyProperty);
  const [step, setStep] = useState<number>(1);
  const [saving, setSaving] = useState<boolean>(false);
  const [config, setConfig] = useState<any>(null);
  const [loadingConfig, setLoadingConfig] = useState<boolean>(false);

  // Fetch configuration whenever gewType changes
  useEffect(() => {
    if (property.gewType) {
      setLoadingConfig(true);
      propertyApi
        .getConditionClassificationConfig(property.gewType)
        .then((config) => {
          setConfig(config);

          // Dynamically initialize features and ratings based on config
          const initialFeatures: Record<
            string,
            { value: boolean; localization: string }
          > = {};
          config.weights?.features?.forEach((f: any) => {
            initialFeatures[f.id] = {
              value: false,
              localization: f.localization,
            };
          });
          config.staticValues?.features?.forEach((f: any) => {
            initialFeatures[f.id] = {
              value: false,
              localization: f.localization,
            };
          });

          const initialRatings: Record<
            string,
            { value: number; localization: string }
          > = {};
          config.weights?.ratings?.forEach((r: any) => {
            initialRatings[r.id] = { value: 1, localization: r.localization };
          });

          setProperty((prev) => ({
            ...prev,
            features: initialFeatures,
            ratings: initialRatings,
          }));
        })
        .catch((err) => {
          console.error('Failed to load condition classification config:', err);
          showError(
            'Failed to load configuration for the selected property type.',
          );
        })
        .finally(() => {
          setLoadingConfig(false);
        });
    }
  }, [property.gewType]);

  // Suggestions state for autocomplete
  const [suggestions, setSuggestions] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] =
    useState<number>(-1);

  const [citySuggestions, setCitySuggestions] = useState<any[]>([]);
  const [isSearchingCity, setIsSearchingCity] = useState(false);
  const [selectedCityIndex, setSelectedCityIndex] = useState<number>(-1);

  const [isGeocoding, setIsGeocoding] = useState<boolean>(false);

  // Function to geocode an address using Google Maps Geocoder
  const geocodeAddress = (
    address: string,
  ): Promise<{ lat: number; lng: number } | null> => {
    return new Promise((resolve) => {
      const googleMaps = (window as any).google;
      if (!googleMaps || !googleMaps.maps || !googleMaps.maps.Geocoder) {
        resolve(null);
        return;
      }

      const geocoder = new googleMaps.maps.Geocoder();
      geocoder.geocode(
        { address, region: 'at' },
        (
          results: google.maps.GeocoderResult[] | null,
          status: google.maps.GeocoderStatus,
        ) => {
          if (status === 'OK' && results && results.length > 0) {
            const location = results[0].geometry.location;
            resolve({
              lat: location.lat(),
              lng: location.lng(),
            });
          } else {
            resolve(null);
          }
        },
      );
    });
  };

  // Refs for debounce timeout
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const debounceCityRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle address input change with debouncing for autocomplete
  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProperty((prev) => ({
      ...prev,
      streetName: value,
      latitude: undefined,
      longitude: undefined,
    }));

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    if (!value) {
      setSuggestions([]);
      setSelectedSuggestionIndex(-1);
      return;
    }

    debounceTimeoutRef.current = setTimeout(async () => {
      setIsSearching(true);
      try {
        const result = await placesApi.getAutocomplete(value);
        setSuggestions(result);
        setSelectedSuggestionIndex(-1);
      } catch (error) {
        console.error(error);
        setSuggestions([]);
        setSelectedSuggestionIndex(-1);
      } finally {
        setIsSearching(false);
      }
    }, 300);
  };

  // Handle city input change with debouncing for autocomplete
  const handleCityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setProperty((prev) => ({
      ...prev,
      city: value,
      latitude: undefined,
      longitude: undefined,
    }));

    if (debounceCityRef.current) {
      clearTimeout(debounceCityRef.current);
    }

    if (!value) {
      setCitySuggestions([]);
      setSelectedCityIndex(-1);
      return;
    }

    debounceCityRef.current = setTimeout(async () => {
      setIsSearchingCity(true);
      try {
        const result = await placesApi.getCities(value);
        setCitySuggestions(result);
        setSelectedCityIndex(-1);
      } catch (error) {
        console.error(error);
        setCitySuggestions([]);
        setSelectedCityIndex(-1);
      } finally {
        setIsSearchingCity(false);
      }
    }, 300);
  };

  // Helper function to find best match from DB for parsed city
  const findBestCityMatch = (
    parsed: { city?: string; sublocality?: string; postalCode?: string },
    dbCities: string[],
  ): string | null => {
    if (!parsed.city || dbCities.length === 0) return null;

    const cityLower = parsed.city.toLowerCase();

    // 1. Check for an exact match (case-insensitive)
    const exactMatch = dbCities.find((c) => c.toLowerCase() === cityLower);
    if (exactMatch) return exactMatch;

    // 2. If there are sublocalities/neighborhoods, try to match them
    if (parsed.sublocality) {
      const subLower = parsed.sublocality.toLowerCase();
      const subMatch = dbCities.find((c) => {
        const cLower = c.toLowerCase();
        return cLower.includes(cityLower) && cLower.includes(subLower);
      });
      if (subMatch) return subMatch;
    }

    // 3. For Vienna, match using postal code
    if (parsed.postalCode && (cityLower === 'wien' || cityLower === 'vienna')) {
      const zip = parsed.postalCode;
      if (zip.startsWith('1') && zip.length === 4) {
        const districtNum = parseInt(zip.substring(1, 3), 10);
        if (districtNum >= 1 && districtNum <= 23) {
          const pattern = `wien ${districtNum}.`;
          const districtMatch = dbCities.find((c) =>
            c.toLowerCase().includes(pattern),
          );
          if (districtMatch) return districtMatch;
        }
      }
    }

    // 4. If single match starting with city name
    if (
      dbCities.length === 1 &&
      dbCities[0].toLowerCase().startsWith(cityLower)
    ) {
      return dbCities[0];
    }

    // 5. Fallback to first matching prefix
    const prefixMatch = dbCities.find((c) =>
      c.toLowerCase().startsWith(cityLower),
    );
    if (prefixMatch) return prefixMatch;

    return null;
  };

  // Helper function to parse address components from Places API response
  const parseAddressComponents = (
    addressComponents: any[],
  ): {
    streetName?: string;
    houseNumber?: string;
    city?: string;
    country?: string;
    sublocality?: string;
    postalCode?: string;
    administrativeArea3?: string;
    administrativeArea2?: string;
  } => {
    const result: {
      streetName?: string;
      houseNumber?: string;
      city?: string;
      country?: string;
      sublocality?: string;
      postalCode?: string;
      administrativeArea3?: string;
      administrativeArea2?: string;
    } = {};

    if (!addressComponents || !Array.isArray(addressComponents)) {
      return result;
    }

    for (const component of addressComponents) {
      const types = component.types || [];
      const longText = component.longText;
      const shortText = component.shortText;
      const text = component.text;
      const value = longText || shortText || text;

      if (types.includes('street_number')) {
        result.houseNumber = value;
      } else if (types.includes('route')) {
        result.streetName = value;
      } else if (types.includes('locality') || types.includes('postal_town')) {
        result.city = value;
      } else if (types.includes('country')) {
        result.country = value;
      } else if (
        types.includes('sublocality') ||
        types.includes('sublocality_level_1') ||
        types.includes('neighborhood')
      ) {
        result.sublocality = value;
      } else if (types.includes('postal_code')) {
        result.postalCode = value;
      } else if (types.includes('administrative_area_level_3')) {
        result.administrativeArea3 = value;
      } else if (types.includes('administrative_area_level_2')) {
        result.administrativeArea2 = value;
      }
    }

    return result;
  };

  // Handle suggestion selection
  const handleSuggestionClick = async (suggestion: any) => {
    const placeId = suggestion.placeId || suggestion.placePrediction?.placeId;
    const address =
      suggestion.text?.text ||
      suggestion.placePrediction?.text?.text ||
      suggestion.text ||
      suggestion.placePrediction?.structuredFormat?.mainText?.text ||
      'Unknown address';

    setSuggestions([]);
    setSelectedSuggestionIndex(-1);
    setCitySuggestions([]);
    setSelectedCityIndex(-1);

    if (placeId) {
      try {
        const details = await placesApi.getPlaceDetails(placeId);

        if (details?.location) {
          const lat = details.location.latitude;
          const lng = details.location.longitude;

          const addressComponents = details.addressComponents || [];
          const parsed = parseAddressComponents(addressComponents);
          const fullAddress = details.formattedAddress || address;

          let matchedCity = '';
          try {
            const cityFromCoordinates = await placesApi.getCityByCoordinates(
              lat,
              lng,
            );
            if (cityFromCoordinates) {
              matchedCity = cityFromCoordinates;
            }
          } catch (err) {
            console.error('Error fetching city by coordinates:', err);
          }

          // Fallback safeguard: if GeoJSON lookup returned null, try the old resolution logic
          if (!matchedCity) {
            const cityCandidates = [
              parsed.city,
              parsed.administrativeArea3,
              parsed.administrativeArea2,
              parsed.sublocality,
            ].filter(Boolean) as string[];

            for (const candidate of cityCandidates) {
              try {
                const dbCities = await placesApi.getCities(candidate);
                const tempParsed = { ...parsed, city: candidate };
                const bestMatch = findBestCityMatch(tempParsed, dbCities);
                if (bestMatch) {
                  matchedCity = bestMatch;
                  break; // Found a valid database match!
                }
              } catch (err) {
                console.error(
                  'Error validating city candidate against database:',
                  err,
                );
              }
            }
          }

          setProperty((prev) => {
            const updated = {
              ...prev,
              address: fullAddress,
              country: 'Österreich',
              latitude: lat,
              longitude: lng,
            };

            if (parsed.streetName !== undefined) {
              updated.streetName = parsed.streetName;
            }
            if (parsed.houseNumber !== undefined) {
              updated.houseNumber = parsed.houseNumber;
            }

            if (matchedCity) {
              updated.city = matchedCity;
            } else {
              updated.city = '';
            }

            return updated;
          });
        } else {
          showError('Location coordinates could not be retrieved.');
          setProperty((prev) => ({ ...prev, address }));
        }
      } catch (error) {
        console.error('Error fetching place details:', error);
        showError('Failed to get address details.');
        setProperty((prev) => ({ ...prev, address }));
      }
    } else {
      setProperty((prev) => ({ ...prev, address }));
    }
  };

  // Handle city suggestion selection
  const handleCitySuggestionClick = (city: string) => {
    setCitySuggestions([]);
    setSelectedCityIndex(-1);

    setProperty((prev) => {
      const updated = {
        ...prev,
        city,
        country: 'Österreich',
      };

      const parts = [];
      if (updated.streetName) parts.push(updated.streetName);
      if (updated.houseNumber) parts.push(updated.houseNumber);
      if (updated.city) parts.push(updated.city);
      if (updated.country) parts.push(updated.country);
      updated.address = parts.join(', ');

      return updated;
    });
  };

  // Cleanup autocomplete debouncers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      if (debounceCityRef.current) {
        clearTimeout(debounceCityRef.current);
      }
    };
  }, []);

  // Keyboard navigation for suggestions dropdown list
  const handleSuggestionsKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    fieldName: 'streetName' | 'city',
  ) => {
    const currentSuggestions =
      fieldName === 'city' ? citySuggestions : suggestions;
    const currentSelectedIndex =
      fieldName === 'city' ? selectedCityIndex : selectedSuggestionIndex;
    const setCurrentSelectedIndex =
      fieldName === 'city' ? setSelectedCityIndex : setSelectedSuggestionIndex;
    const setCurrentSuggestions =
      fieldName === 'city' ? setCitySuggestions : setSuggestions;

    if (currentSuggestions.length === 0) return;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setCurrentSelectedIndex((prev) =>
        prev < currentSuggestions.length - 1 ? prev + 1 : prev,
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setCurrentSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (
        currentSelectedIndex >= 0 &&
        currentSelectedIndex < currentSuggestions.length
      ) {
        if (fieldName === 'city') {
          handleCitySuggestionClick(currentSuggestions[currentSelectedIndex]);
        } else {
          handleSuggestionClick(currentSuggestions[currentSelectedIndex]);
        }
      }
    } else if (e.key === 'Escape') {
      setCurrentSuggestions([]);
      setCurrentSelectedIndex(-1);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const createData = { ...property };
      delete (createData as any).id;
      delete (createData as any).gain;
      delete (createData as any).gainPercentage;
      const newProperty = await propertyApi.create(createData as any);
      showSuccess('Property created successfully');
      navigate(`/properties/${newProperty.id}`);
    } catch (error: any) {
      showError(error.message || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const renderFeatureToggle = (fieldName: string, label: string) => {
    const value = property.features?.[fieldName]?.value === true;
    return (
      <div className="detail-item" key={fieldName}>
        <label>{label}</label>
        <ToggleButtonGroup
          value={String(value)}
          exclusive
          onChange={(_event, newValue) => {
            if (newValue !== null) {
              const bValue = newValue === 'true';
              setProperty((prev) => ({
                ...prev,
                features: {
                  ...prev.features,
                  [fieldName]: {
                    value: bValue,
                    localization: label,
                  },
                },
              }));
            }
          }}
          sx={{
            '& .MuiToggleButton-root': {
              color: '#A7A3E2',
              borderColor: 'rgba(147, 142, 211, 0.2)',
              fontFamily: "'Inter', sans-serif",
              textTransform: 'none',
              padding: '8px 16px',
              '&.Mui-selected': {
                color: '#FFF',
                backgroundColor: 'rgba(87, 26, 255, 0.25)',
                borderColor: 'rgba(87, 26, 255, 0.6)',
                '&:hover': {
                  backgroundColor: 'rgba(87, 26, 255, 0.35)',
                },
              },
              '&:hover': {
                backgroundColor: 'rgba(147, 142, 211, 0.08)',
              },
            },
          }}
        >
          <ToggleButton value="true">Ja</ToggleButton>
          <ToggleButton value="false">Nein</ToggleButton>
        </ToggleButtonGroup>
      </div>
    );
  };

  const renderRatingInput = (fieldName: string, label: string) => {
    const value = property.ratings?.[fieldName]?.value || 1;
    return (
      <div
        className="detail-item"
        key={fieldName}
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <label style={{ margin: 0 }}>{label}</label>
        <Rating
          name={fieldName}
          value={value}
          precision={0.5}
          size="medium"
          sx={{
            color: '#faaf00',
            fontSize: '1.5rem',
            '& .MuiRating-iconFilled': { color: '#faaf00' },
            '& .MuiRating-iconHover': { color: '#faaf00' },
          }}
          onChange={(_event, newValue) => {
            if (newValue !== null) {
              setProperty((prev) => ({
                ...prev,
                ratings: {
                  ...prev.ratings,
                  [fieldName]: {
                    value: newValue || 1,
                    localization: label,
                  },
                },
              }));
            }
          }}
        />
      </div>
    );
  };

  // Step Navigations
  const nextStep = async () => {
    if (step === 1 && !property.gewType) return;
    if (step === 2) {
      if (!isStep2Valid()) return;
      if (isGeocoding) return;

      // If coordinates are missing, fetch them before proceeding
      if (
        property.latitude === undefined ||
        property.longitude === undefined ||
        property.latitude === null ||
        property.longitude === null
      ) {
        setIsGeocoding(true);
        try {
          const parts = [];
          if (property.streetName) parts.push(property.streetName);
          if (property.houseNumber) parts.push(property.houseNumber);
          if (property.city) parts.push(property.city);
          parts.push(property.country || 'Österreich');
          const fullAddress = parts.join(', ');

          const location = await geocodeAddress(fullAddress);
          if (location) {
            setProperty((prev) => ({
              ...prev,
              address: fullAddress,
              latitude: location.lat,
              longitude: location.lng,
            }));
          } else {
            console.warn('Geocoding returned no result');
          }
        } catch (error) {
          console.error('Error geocoding address:', error);
        } finally {
          setIsGeocoding(false);
        }
      }
    }
    setStep((prev) => prev + 1);
  };

  const prevStep = () => {
    setStep((prev) => prev - 1);
  };

  // Step Validations
  const isStep2Valid = () => {
    const hasKaufpreis = property.kaufpreis !== undefined && property.kaufpreis !== null;
    const hasKaufdatum = !!property.kaufdatum;
    const isPurchaseDataValid = (hasKaufpreis && hasKaufdatum) || (!hasKaufpreis && !hasKaufdatum);

    return (
      !!property.streetName?.trim() &&
      !!property.houseNumber?.trim() &&
      !!property.city?.trim() &&
      property.area !== undefined &&
      property.area > 0 &&
      isPurchaseDataValid
    );
  };

  return (
    <div className="property-page">
      <div className="property-container">
        <header className="property-header">
          <button
            className="back-button"
            onClick={() => navigate('/dashboard')}
          >
            ← Back to Dashboard
          </button>
        </header>

        <div className="property-content">
          <h1
            className="property-title"
            style={{ textAlign: 'center', marginBottom: '24px' }}
          >
            Add New Property
          </h1>

          {/* Stepper Progress Indicator */}
          <div className="wizard-stepper">
            <div className={`step-indicator ${step >= 1 ? 'active' : ''}`}>
              <span className="step-number">1</span>
              <span className="step-label">Immobilientyp</span>
            </div>
            <div className={`step-line ${step >= 2 ? 'active' : ''}`}></div>
            <div className={`step-indicator ${step >= 2 ? 'active' : ''}`}>
              <span className="step-number">2</span>
              <span className="step-label">Adresse & Preis</span>
            </div>
            <div className={`step-line ${step >= 3 ? 'active' : ''}`}></div>
            <div className={`step-indicator ${step >= 3 ? 'active' : ''}`}>
              <span className="step-number">3</span>
              <span className="step-label">Details & Zustand</span>
            </div>
          </div>

          <div style={{ maxWidth: '800px', margin: '0 auto' }}>
            {/* Step 1: GewType Selection */}
            {step === 1 && (
              <div className="detail-section" style={{ width: '100%' }}>
                <h2>Wählen Sie den Immobilientyp</h2>
                <div className="gewtype-grid">
                  {/* Card 1: Wohnung gesamt */}
                  <div
                    className={`gewtype-card ${property.gewType === GewType.GESAMT ? 'active' : ''}`}
                    onClick={() =>
                      setProperty((prev) => ({
                        ...prev,
                        gewType: GewType.GESAMT,
                      }))
                    }
                  >
                    <div className="gewtype-card-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <rect
                          x="4"
                          y="2"
                          width="16"
                          height="20"
                          rx="2"
                          ry="2"
                        />
                        <line x1="9" y1="22" x2="9" y2="16" />
                        <line x1="15" y1="22" x2="15" y2="16" />
                        <line x1="9" y1="16" x2="15" y2="16" />
                        <path d="M8 6h2v2H8V6zm6 0h2v2h-2V6zm-6 5h2v2H8v-2zm6 0h2v2h-2v-2z" />
                      </svg>
                    </div>
                    <div className="gewtype-card-title">{GewType.GESAMT}</div>
                  </div>

                  {/* Card 2: Wohnung Neubau */}
                  <div
                    className={`gewtype-card ${property.gewType === GewType.NEUBAU ? 'active' : ''}`}
                    onClick={() =>
                      setProperty((prev) => ({
                        ...prev,
                        gewType: GewType.NEUBAU,
                      }))
                    }
                  >
                    <div className="gewtype-card-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 21h18" />
                        <path d="M5 21V7l8-4v18" />
                        <path d="M19 21V11l-6-2v12" />
                        <path d="M9 9h2v2H9V9zm0 4h2v2H9v-2zm0 4h2v2H9v-2z" />
                      </svg>
                    </div>
                    <div className="gewtype-card-title">{GewType.NEUBAU}</div>
                  </div>

                  {/* Card 3: Wohnung Bestand */}
                  <div
                    className={`gewtype-card ${property.gewType === GewType.BESTAND ? 'active' : ''}`}
                    onClick={() =>
                      setProperty((prev) => ({
                        ...prev,
                        gewType: GewType.BESTAND,
                      }))
                    }
                  >
                    <div className="gewtype-card-icon">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="48"
                        height="48"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                        <polyline points="9 22 9 12 15 12 15 22" />
                      </svg>
                    </div>
                    <div className="gewtype-card-title">{GewType.BESTAND}</div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Address, Area, Price */}
            {step === 2 && (
              <div className="detail-section" style={{ width: '100%' }}>
                <h2>Adresse & Finanzdaten</h2>
                <div className="detail-list">
                  {/* Address inputs */}
                  <div className="detail-item-row">
                    <div
                      className="detail-item"
                      style={{ position: 'relative', flex: '1 1 80%' }}
                    >
                      <label>Straße</label>
                      <input
                        type="text"
                        className="wizard-input"
                        value={property.streetName || ''}
                        onChange={handleAddressChange}
                        onKeyDown={(e) =>
                          handleSuggestionsKeyDown(e, 'streetName')
                        }
                        placeholder="Straßenname eingeben..."
                        onBlur={() => {
                          setTimeout(() => {
                            setSuggestions([]);
                            setSelectedSuggestionIndex(-1);
                          }, 250);
                        }}
                      />
                      {isSearching && (
                        <div className="suggestion-searching">
                          <LoadingSpinner size="small" color="white" />
                          <span>Wird gesucht...</span>
                        </div>
                      )}
                      {suggestions.length > 0 && (
                        <ul className="suggestions-container">
                          {suggestions.map((suggestion, index) => {
                            const displayText =
                              suggestion.text?.text ||
                              suggestion.placePrediction?.text?.text ||
                              suggestion.text ||
                              suggestion.placePrediction?.structuredFormat
                                ?.mainText?.text ||
                              'Unknown';
                            const secondaryText =
                              suggestion.placePrediction?.structuredFormat
                                ?.secondaryText?.text;
                            const isSelected =
                              index === selectedSuggestionIndex;

                            return (
                              <li
                                key={index}
                                onClick={() =>
                                  handleSuggestionClick(suggestion)
                                }
                                onMouseDown={(e) => e.preventDefault()}
                                onMouseEnter={() =>
                                  setSelectedSuggestionIndex(index)
                                }
                                className={`suggestion-item ${isSelected ? 'selected' : ''}`}
                              >
                                <div className="suggestion-main-text">
                                  {displayText}
                                </div>
                                {secondaryText && (
                                  <div className="suggestion-secondary-text">
                                    {secondaryText}
                                  </div>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    <div className="detail-item" style={{ flex: '0 0 20%' }}>
                      <label>Nummer</label>
                      <input
                        type="text"
                        className="wizard-input"
                        value={property.houseNumber || ''}
                        placeholder="Nr."
                        onChange={(e) => {
                          const val = e.target.value;
                          setProperty((prev) => {
                            const updated = {
                              ...prev,
                              houseNumber: val,
                              latitude: undefined,
                              longitude: undefined,
                            };
                            const parts = [];
                            if (updated.streetName)
                              parts.push(updated.streetName);
                            if (updated.houseNumber)
                              parts.push(updated.houseNumber);
                            if (updated.city) parts.push(updated.city);
                            if (updated.country) parts.push(updated.country);
                            updated.address =
                              parts.join(', ') || prev.address || '';
                            return updated;
                          });
                        }}
                      />
                    </div>
                  </div>

                  <div className="detail-item-row">
                    <div
                      className="detail-item"
                      style={{ position: 'relative' }}
                    >
                      <label>Ort</label>
                      <input
                        type="text"
                        className="wizard-input"
                        value={property.city || ''}
                        onChange={handleCityChange}
                        onKeyDown={(e) => handleSuggestionsKeyDown(e, 'city')}
                        placeholder="Ort eingeben..."
                        onBlur={() => {
                          setTimeout(() => {
                            setCitySuggestions([]);
                            setSelectedCityIndex(-1);
                          }, 250);
                        }}
                      />
                      {isSearchingCity && (
                        <div className="suggestion-searching">
                          <LoadingSpinner size="small" color="white" />
                          <span>Wird gesucht...</span>
                        </div>
                      )}
                      {citySuggestions.length > 0 && (
                        <ul className="suggestions-container">
                          {citySuggestions.map((suggestion, index) => {
                            const displayText = suggestion;
                            const isSelected = index === selectedCityIndex;

                            return (
                              <li
                                key={index}
                                onClick={() =>
                                  handleCitySuggestionClick(suggestion)
                                }
                                onMouseDown={(e) => e.preventDefault()}
                                onMouseEnter={() => setSelectedCityIndex(index)}
                                className={`suggestion-item ${isSelected ? 'selected' : ''}`}
                              >
                                <div className="suggestion-main-text">
                                  {displayText}
                                </div>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                    <div className="detail-item">
                      <label>Land</label>
                      <input
                        type="text"
                        className="wizard-input"
                        value="Österreich"
                        disabled
                        style={{
                          backgroundColor: 'rgba(147, 142, 211, 0.02)',
                          cursor: 'not-allowed',
                        }}
                      />
                    </div>
                  </div>

                  {/* Area and purchase price */}
                  <div className="detail-item-row">
                    <div className="detail-item">
                      <label>Wohnfläche (m²)</label>
                      <input
                        type="number"
                        className="wizard-input"
                        value={property.area !== undefined ? property.area : ''}
                        onChange={(e) => {
                          const val =
                            e.target.value === ''
                              ? undefined
                              : parseFloat(e.target.value);
                          setProperty((prev) => ({ ...prev, area: val }));
                        }}
                        placeholder="Fläche in m² eingeben..."
                        min="0"
                        step="any"
                      />
                    </div>
                    <div className="detail-item">
                      <label>Kaufpreis (€)</label>
                      <input
                        type="number"
                        className="wizard-input"
                        value={
                          property.kaufpreis !== undefined
                            ? property.kaufpreis
                            : ''
                        }
                        onChange={(e) => {
                          const val =
                            e.target.value === ''
                              ? undefined
                              : parseFloat(e.target.value);
                          setProperty((prev) => ({
                            ...prev,
                            kaufpreis: val,
                            gain: (prev.currentValue || 0) - (val || 0),
                            gainPercentage:
                              val && val > 0
                                ? (((prev.currentValue || 0) - val) / val) * 100
                                : 0,
                          }));
                        }}
                        placeholder="Kaufpreis in € eingeben..."
                        min="0"
                        step="any"
                      />
                    </div>
                  </div>

                  <div className="detail-item-row">
                    <div className="detail-item">
                      <label>Kaufdatum</label>
                      <input
                        type="date"
                        className="wizard-input"
                        value={property.kaufdatum || ''}
                        onChange={(e) => {
                          setProperty((prev) => ({
                            ...prev,
                            kaufdatum: e.target.value || undefined,
                          }));
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Step 3: Features & Ratings */}
            {step === 3 && (
              <div className="detail-section" style={{ width: '100%' }}>
                <h2>Ausstattung & Zustand</h2>
                {loadingConfig ? (
                  <div style={{ textAlign: 'center', padding: '2rem' }}>
                    <LoadingSpinner size="medium" color="white" />
                    <p style={{ marginTop: '12px' }}>
                      Konfiguration wird geladen...
                    </p>
                  </div>
                ) : !config ? (
                  <p style={{ color: '#ff6b6b', textAlign: 'center' }}>
                    Konfiguration konnte nicht geladen werden.
                  </p>
                ) : (
                  <div className="detail-list">
                    {(config.weights?.features?.length > 0 || config.staticValues?.features?.length > 0) && (
                      <>
                        <div className="detail-section-subtitle">
                          Ausstattung & Lage
                        </div>
                        <div className="attributes-grid">
                          {config.weights?.features?.map((f: any) =>
                            renderFeatureToggle(f.id, f.localization),
                          )}
                          {config.staticValues?.features?.map((f: any) =>
                            renderFeatureToggle(f.id, f.localization),
                          )}
                        </div>
                      </>
                    )}

                    {config.weights?.ratings?.length > 0 && (
                      <>
                        <div
                          className="detail-section-subtitle"
                          style={{ marginTop: '24px' }}
                        >
                          Zustand & Qualität
                        </div>
                        <div
                          className="detail-item-row"
                          style={{
                            flexDirection: 'column',
                            gap: '1rem',
                            alignItems: 'stretch',
                          }}
                        >
                          {config.weights.ratings.map((r: any) =>
                            renderRatingInput(r.id, r.localization),
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* Stepper Navigation Buttons */}
            <div className="wizard-buttons-container">
              <div>
                {step > 1 && (
                  <button
                    className="action-button secondary"
                    onClick={prevStep}
                  >
                    Zurück
                  </button>
                )}
              </div>
              <div>
                {step < 3 ? (
                  <button
                    className="action-button primary"
                    onClick={nextStep}
                    disabled={
                      (step === 1 && !property.gewType) ||
                      (step === 2 && (!isStep2Valid() || isGeocoding))
                    }
                  >
                    {isGeocoding ? 'Wird überprüft...' : 'Weiter'}
                  </button>
                ) : (
                  <button
                    className="action-button primary"
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? 'Wird gespeichert...' : 'Immobilie speichern'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CreatePropertyPage;
